// Composants récurrents du handoff : logo, pastille de statut, case, segmented, barre d'onglets,
// champ, bandeau d'aide à la décision, états vide / chargement / erreur.
import { NAVIGATION_ADVISORY, STATUS_DISPLAY, type WindowStatus } from '@crenow/shared';
import { useCallback, useEffect, useId, useRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { NavLink } from 'react-router';
import { api, errorMessage } from './api.ts';

export function Logo() {
  return (
    <div className="logo" aria-hidden="true">
      <span />
      <span />
      <span />
      <span />
    </div>
  );
}

/** Statut : couleur + glyphe + libellé, jamais la couleur seule. */
export function StatusPill({ status }: { status: WindowStatus }) {
  const display = STATUS_DISPLAY[status];
  return (
    <span className={`status st-${display.token}`}>
      <span aria-hidden="true">{display.glyph}</span>
      {display.label}
    </span>
  );
}

export function Box({ on, small = false }: { on: boolean; small?: boolean }) {
  return (
    <span className={`box${small ? ' small' : ''}${on ? ' on' : ''}`} aria-hidden="true">
      {on ? '✓' : ''}
    </span>
  );
}

export function Segmented<T extends string | number>({ value, options, onChange, label }: { value: T; options: { value: T; label: string }[]; onChange: (value: T) => void; label: string }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button key={option.value} type="button" aria-pressed={option.value === value} onClick={() => onChange(option.value)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

const TABS = [
  { to: '/', label: 'Fenêtres', end: true },
  { to: '/creer', label: 'Créer', end: false },
  { to: '/lieux', label: 'Lieux', end: false },
  { to: '/liens', label: 'Liens', end: false },
];

export function TabBar() {
  return (
    <nav className="tabbar" aria-label="Navigation principale">
      {TABS.map((tab) => (
        <NavLink key={tab.to} to={tab.to} end={tab.end}>
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}

/** Champ avec libellé au-dessus et erreur reliée (`aria-describedby`). */
export function Field({ label, error, className = 'input', ...input }: { label: string; error?: string | null } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <label className="field">
      <span>{label}</span>
      <input className={className} aria-invalid={error ? true : undefined} aria-describedby={error ? `${id}-error` : undefined} {...input} />
      {error && (
        <span id={`${id}-error`} className="field-error">
          {error}
        </span>
      )}
    </label>
  );
}

export function MarineBanner() {
  return (
    <div className="marine">
      <span>{NAVIGATION_ADVISORY.label}</span>
      <a href={NAVIGATION_ADVISORY.bulletinUrl} target="_blank" rel="noreferrer">
        {NAVIGATION_ADVISORY.bulletinLabel} ↗
      </a>
    </div>
  );
}

export function Loading({ label = 'Chargement…' }: { label?: string }) {
  return (
    <p className="loading" role="status">
      {label}
    </p>
  );
}

export function FormError({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <p className="form-error" role="alert">
      {error}
    </p>
  );
}

export interface Resource<T> {
  data: T | undefined;
  error: string | null;
  reload: () => Promise<void>;
  setData: (update: (current: T | undefined) => T | undefined) => void;
}

/** Données d'une route GET de l'API, rechargeables ; seule la dernière réponse compte. */
export function useResource<T>(path: string | null): Resource<T> {
  const [data, setState] = useState<T | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const reload = useCallback(async () => {
    if (!path) return;
    const at = ++generation.current;
    try {
      const result = await api<T>('GET', path);
      if (at !== generation.current) return;
      setState(result);
      setError(null);
    } catch (caught) {
      if (at === generation.current) setError(errorMessage(caught));
    }
  }, [path]);
  useEffect(() => {
    // Autre ressource : rien de l'ancienne ne reste affiché.
    setState(undefined);
    setError(null);
    void reload();
    // Démontage : les réponses en vol sont ignorées.
    return () => void generation.current++;
  }, [reload]);
  return { data, error, reload, setData: setState };
}

/** Retour visuel bref (« Copié ✓ », « Mdp temporaire envoyé ») : clé active pendant `ms`. */
export function useFlash(ms: number): [string | null, (key: string) => void] {
  const [key, setKey] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return [
    key,
    (next) => {
      setKey(next);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setKey(null), ms);
    },
  ];
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}
